import type {
  ApiResponse,
  Club,
  Court,
  CourtFormData,
  Location,
  LocationFormData,
  Notification,
  OrgEntitlements,
  Region,
} from '@court-sync/shared';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './apiClient';

export interface ClubDashboardOverview {
  clubId: string;
  clubName: string;
  activeMembers: number;
  courtHireBookingsToday: number;
  membershipsActive: number;
  courtsTotal: number;
}

export interface UpdateClubPayload {
  name?: string;
  slug?: string;
  timezone?: string;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  guestCheckoutEnabled?: boolean;
  isActive?: boolean;
  region?: Region | null;
  stateOrProvince?: string | null;
  membershipYearStartMonth?: number;
  membershipYearStartDay?: number;
  membershipOnlineJoinEnabled?: boolean;
  membershipSurchargeEnabled?: boolean;
  acknowledgeSurchargePassThrough?: boolean;
  description?: string | null;
  bannerUrl?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  subdomain?: string | null;
  subdomainEnabled?: boolean;
  websiteEmbedEnabled?: boolean;
  embedAllowedOrigins?: string[];
}

export const clubService = {
  async getUserClubs(): Promise<ApiResponse<Club[]>> {
    return apiGet<Club[]>('/clubs/user');
  },

  async getClub(id: string): Promise<ApiResponse<Club>> {
    return apiGet<Club>(`/clubs/${id}`);
  },

  async updateClub(id: string, data: UpdateClubPayload): Promise<ApiResponse<Club>> {
    return apiPatch<Club>(`/clubs/${id}`, data);
  },

  async checkSubdomainAvailability(
    label: string,
    excludeClubId?: string
  ): Promise<ApiResponse<{ available: boolean; label: string }>> {
    const params = excludeClubId ? `?excludeClubId=${encodeURIComponent(excludeClubId)}` : '';
    return apiGet<{ available: boolean; label: string }>(
      `/public/clubs/subdomain/${encodeURIComponent(label)}/check${params}`
    );
  },

  async updateSubdomainSettings(
    clubId: string,
    data: { subdomain: string; subdomainEnabled: boolean }
  ): Promise<ApiResponse<Club>> {
    return apiPatch<Club>(`/clubs/${clubId}`, data);
  },

  async getOrganizationEntitlements(): Promise<ApiResponse<OrgEntitlements>> {
    return apiGet<OrgEntitlements>('/organizations/entitlements');
  },

  async getDashboardOverview(clubId: string): Promise<ApiResponse<ClubDashboardOverview>> {
    return apiGet<ClubDashboardOverview>(`/clubs/${clubId}/dashboard`);
  },

  async getRecentActivity(clubId: string, limit = 15): Promise<ApiResponse<Notification[]>> {
    return apiGet<Notification[]>(`/clubs/${clubId}/activity`, { params: { limit } });
  },

  /** Courts owned by the club. */
  async getClubCourts(clubId: string): Promise<ApiResponse<Court[]>> {
    return apiGet<Court[]>(`/clubs/${clubId}/courts`);
  },

  /** Club facility management — courts and locations owned by the club. */
  async getFacilityCourts(clubId: string, includeInactive = false): Promise<ApiResponse<Court[]>> {
    return apiGet<Court[]>(`/clubs/${clubId}/facility/courts`, {
      params: { includeInactive: includeInactive ? 'true' : undefined },
    });
  },

  async createFacilityCourt(
    clubId: string,
    payload: CourtFormData
  ): Promise<ApiResponse<Court | null>> {
    return apiPost<Court | null>(`/clubs/${clubId}/facility/courts`, payload);
  },

  async updateFacilityCourt(
    clubId: string,
    courtId: string,
    payload: CourtFormData
  ): Promise<ApiResponse<Court | null>> {
    return apiPut<Court | null>(`/clubs/${clubId}/facility/courts/${courtId}`, payload);
  },

  async deleteFacilityCourt(clubId: string, courtId: string): Promise<ApiResponse> {
    return apiDelete(`/clubs/${clubId}/facility/courts/${courtId}`);
  },

  async getFacilityLocations(
    clubId: string,
    includeInactive = false
  ): Promise<ApiResponse<Location[]>> {
    return apiGet<Location[]>(`/clubs/${clubId}/facility/locations`, {
      params: { includeInactive: includeInactive ? 'true' : undefined },
    });
  },

  async createFacilityLocation(
    clubId: string,
    payload: LocationFormData
  ): Promise<ApiResponse<Location | null>> {
    return apiPost<Location | null>(`/clubs/${clubId}/facility/locations`, payload);
  },

  async updateFacilityLocation(
    clubId: string,
    locationId: string,
    payload: LocationFormData
  ): Promise<ApiResponse<Location | null>> {
    return apiPut<Location | null>(`/clubs/${clubId}/facility/locations/${locationId}`, payload);
  },

  async deleteFacilityLocation(clubId: string, locationId: string): Promise<ApiResponse> {
    return apiDelete(`/clubs/${clubId}/facility/locations/${locationId}`);
  },

  async getClubSports(clubId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        clubId: string;
        sportId: string;
        customLabel?: string | null;
        isPrimary: boolean;
        isActive: boolean;
      }>
    >
  > {
    return apiGet(`/clubs/${clubId}/sports`);
  },

  async updateClubSports(
    clubId: string,
    sports: Array<{
      sportId: string;
      customLabel?: string | null;
      isPrimary?: boolean;
      isActive?: boolean;
    }>
  ): Promise<ApiResponse<null>> {
    return apiPut<null>(`/clubs/${clubId}/sports`, { sports });
  },

  async getAcademyCourtAccess(clubId: string): Promise<
    ApiResponse<Array<{ academyId: string; academyName: string; courtIds: string[] }>>
  > {
    return apiGet(`/clubs/${clubId}/academy-court-access`);
  },

  async updateAcademyCourtAssignments(
    clubId: string,
    academyId: string,
    courtIds: string[]
  ): Promise<ApiResponse<null>> {
    return apiPut<null>(`/clubs/${clubId}/academies/${academyId}/court-assignments`, {
      courtIds,
    });
  },
};
